/**
 * Competitions section page.
 *
 * The top-level surface is now intentionally small: competitions first, invite
 * lookup second. Entries, squads, leaderboard, results, and settings live
 * inside the selected competition workspace so admins reason from the thing
 * they are operating.
 */

import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/utils';

import { AllCompetitionsTab } from './AllCompetitionsTab';
import { InviteInspectorTab } from './InviteInspectorTab';

const TABS = [
  { id: 'all', label: 'Competitions' },
  { id: 'invites', label: 'Invite link inspector' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function CompetitionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = tabParam === 'invites' ? 'invites' : 'all';

  const setTab = (id: TabId) => {
    const next = new URLSearchParams();
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader title="Competitions" />
      <div className="px-8 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-1 border-b">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                activeTab === t.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'all' ? <AllCompetitionsTab /> : null}
        {activeTab === 'invites' ? <InviteInspectorTab /> : null}
      </div>
    </>
  );
}
